import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, CheckCircle2, XCircle, RotateCcw, Printer, Camera, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/** Buyer grades their answers against the tutor's answer key. */
const MarketplaceGradePage = () => {
  const { purchaseId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [purchase, setPurchase] = useState(null);
  const [worksheet, setWorksheet] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState('choose'); // choose | manual | photo
  const photoInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const mine = await api.marketplaceMyPurchases();
      const p = mine.purchases.find((x) => x.id === purchaseId);
      if (!p) { toast.error('Purchase not found'); navigate('/marketplace'); return; }
      const w = mine.worksheets[p.worksheet_id];
      setPurchase(p);
      setWorksheet(w);
      setAnswers(new Array(w?.num_questions || 0).fill(null));
      const g = await api.marketplaceMyGrades(purchaseId);
      setHistory(g.grades || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    }
  }, [purchaseId, navigate]);

  useEffect(() => { load(); }, [load]);

  // Deep-linked "Scan QR → upload photo" flow
  useEffect(() => {
    if (searchParams.get('upload') === '1') {
      setMode('photo');
    }
  }, [searchParams]);

  const setAns = (idx, letter) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[idx] = next[idx] === letter ? null : letter;
      return next;
    });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.marketplaceGrade(purchaseId, answers);
      setResult(r);
      toast.success(`Score: ${r.score_correct}/${r.score_total} (${r.score_pct}%)`);
      const g = await api.marketplaceMyGrades(purchaseId);
      setHistory(g.grades || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Grading failed');
    } finally { setSubmitting(false); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const r = await api.marketplaceGradePhoto(purchaseId, file);
      setResult(r);
      toast.success(`Auto-graded: ${r.score_correct}/${r.score_total} (${r.score_pct}%)`);
      const g = await api.marketplaceMyGrades(purchaseId);
      setHistory(g.grades || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not read the answer sheet');
    } finally {
      setUploading(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const reset = () => {
    setResult(null);
    setAnswers(new Array(worksheet?.num_questions || 0).fill(null));
    setMode('choose');
  };

  const downloadPurchased = () => {
    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/marketplace/worksheets/${worksheet.id}/pdf?token=${encodeURIComponent(token)}`;
    window.open(url, '_blank', 'noopener');
  };

  if (!worksheet) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/marketplace')} className="inline-flex items-center gap-2 text-slate-700 hover:text-slate-900" data-testid="grade-back">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="text-sm font-semibold text-slate-700">Grade my answers</div>
          <div />
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 pt-6 space-y-4">
        <Card className="p-5 rounded-2xl">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-xs text-slate-500">{worksheet.level} • {worksheet.num_questions} MCQs</div>
              <h1 className="text-xl font-bold text-slate-900">{worksheet.title}</h1>
              <div className="text-xs text-slate-500">by {worksheet.tutor_name}</div>
            </div>
            <div className="flex flex-col gap-2">
              <Button size="sm" variant="outline" onClick={downloadPurchased} data-testid="grade-download">Open worksheet</Button>
              <Button size="sm" variant="outline" onClick={() => navigate(`/marketplace/print/${purchaseId}`)} data-testid="grade-print-sheet">
                <Printer className="w-4 h-4 mr-1" /> Bubble sheet
              </Button>
            </div>
          </div>
        </Card>

        {!result && mode === 'choose' && (
          <Card className="p-6 rounded-2xl" data-testid="grade-mode-choose">
            <h3 className="font-semibold text-slate-900 mb-3">How would you like to grade?</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button onClick={() => setMode('photo')} data-testid="grade-mode-photo"
                className="text-left border-2 border-slate-200 hover:border-amber-400 rounded-xl p-4 transition">
                <Camera className="w-6 h-6 text-amber-600 mb-2" />
                <div className="font-semibold text-slate-900">Snap a photo</div>
                <div className="text-sm text-slate-600">Print the bubble sheet, fill it in, snap a photo — we'll auto-grade with AI vision.</div>
              </button>
              <button onClick={() => setMode('manual')} data-testid="grade-mode-manual"
                className="text-left border-2 border-slate-200 hover:border-amber-400 rounded-xl p-4 transition">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 mb-2" />
                <div className="font-semibold text-slate-900">Tap the letters</div>
                <div className="text-sm text-slate-600">Enter your answers one by one right here — instant grade, no printing.</div>
              </button>
            </div>
          </Card>
        )}

        {!result && mode === 'photo' && (
          <Card className="p-6 rounded-2xl" data-testid="grade-photo-panel">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-slate-900">Upload a photo of your filled bubble sheet</h3>
                <p className="text-sm text-slate-600">Well-lit, whole sheet visible, no tilt. HEIC/JPEG/PNG all work.</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setMode('choose')} data-testid="grade-photo-cancel">Back</Button>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => navigate(`/marketplace/print/${purchaseId}`)} data-testid="grade-photo-print">
                <Printer className="w-4 h-4 mr-2" /> Print bubble sheet first
              </Button>
              <label className="inline-flex">
                <input ref={photoInputRef} type="file" accept="image/*" capture="environment" hidden
                  onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="grade-photo-input" />
                <Button asChild disabled={uploading} className="bg-amber-600 hover:bg-amber-700">
                  <span onClick={() => photoInputRef.current?.click()} data-testid="grade-photo-upload">
                    {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Reading…</> : <><Camera className="w-4 h-4 mr-2" /> Take/Upload photo</>}
                  </span>
                </Button>
              </label>
            </div>
            {uploading && <p className="text-xs text-slate-500 mt-3">GPT-5.2 vision is reading your answer sheet — this usually takes ~5-10 seconds.</p>}
          </Card>
        )}

        {!result && mode === 'manual' && (
          <Card className="p-5 rounded-2xl" data-testid="grade-manual-panel">
            <div className="flex items-start justify-between mb-3">
              <p className="text-sm text-slate-600">Select the letter you answered for each question, then submit to grade.</p>
              <Button size="sm" variant="ghost" onClick={() => setMode('choose')}>Back</Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {answers.map((ans, i) => (
                <div key={i} className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2" data-testid={`grade-row-${i}`}>
                  <span className="text-sm font-semibold text-slate-700 w-8">Q{i + 1}</span>
                  {['A','B','C','D'].map((L) => (
                    <button key={L} type="button" onClick={() => setAns(i, L)}
                      className={`w-8 h-8 rounded-full border-2 text-sm font-semibold transition ${ans === L ? 'bg-amber-600 text-white border-amber-700' : 'bg-white text-slate-700 border-slate-300 hover:border-amber-400'}`}
                      data-testid={`grade-q${i}-${L}`}>
                      {L}
                    </button>
                  ))}
                </div>
              ))}
            </div>
            <div className="mt-4">
              <Button onClick={submit} disabled={submitting} className="bg-amber-600 hover:bg-amber-700" data-testid="grade-submit">
                {submitting ? 'Grading…' : 'Submit for grading'}
              </Button>
            </div>
          </Card>
        )}

        {result && (
          <Card className="p-6 rounded-2xl" data-testid="grade-result">
            <div className="text-center mb-4">
              <div className="text-5xl font-bold text-emerald-700">{result.score_correct}<span className="text-slate-400 text-2xl">/{result.score_total}</span></div>
              <div className="text-slate-600">{result.score_pct}% correct{result.source === 'photo' ? ' · read from photo' : ''}</div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {result.per_question.map((q) => (
                <div key={q.q} className={`p-2 rounded-lg text-center text-sm ${q.is_correct ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800'}`}>
                  <div className="font-semibold">Q{q.q}</div>
                  <div className="flex justify-center items-center gap-1 mt-0.5">
                    {q.is_correct
                      ? <CheckCircle2 className="w-4 h-4" />
                      : <XCircle className="w-4 h-4" />}
                    <span>{q.submitted || '—'} <span className="text-slate-500">/ {q.expected}</span></span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex gap-2 justify-center">
              <Button variant="outline" onClick={reset} data-testid="grade-retry"><RotateCcw className="w-4 h-4 mr-2" /> Try again</Button>
              <Button onClick={() => navigate('/marketplace')} className="bg-amber-600 hover:bg-amber-700">Back to library</Button>
            </div>
          </Card>
        )}

        {history.length > 0 && (
          <Card className="p-4 rounded-2xl">
            <div className="text-sm font-semibold text-slate-700 mb-2">Previous attempts</div>
            <ul className="text-sm text-slate-600 space-y-1">
              {history.slice(0, 5).map((h) => (
                <li key={h.id} className="flex justify-between">
                  <span>{new Date(h.graded_at).toLocaleString()}{h.source === 'photo' ? ' · photo' : ''}</span>
                  <span className="font-semibold">{h.score_correct}/{h.score_total} • {h.score_pct}%</span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MarketplaceGradePage;
